import React, { useState, useEffect, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import * as fromQuestionActions from 'store/actions/questions.action'
import { IMainState } from 'store/states'
import { IQuestionData } from 'models/question.model'
import { BlockTypes } from 'enum/block-type.enum'
import { IQuestionLikertModel } from 'models/questionLikert.model'
import { IOpenEndedModel } from 'models/questionOpenEnded.model'
import { Confirmation } from '../Confirmation'
import { SurveyQuestionRows } from '../question-rows'
import { SurveyQuestionModal } from '../question-modal'

interface ISurveyQuestions {
  blockId: number | undefined
  instanceId: number | undefined
  /** Bank actions rendered next to "Add question". */
  toolbar?: ReactNode
}

const typeLabel = (type: string, translate: (key: string) => string) => {
  if (!type) return ''
  const key = type[0].toLowerCase() + type.substring(1).replace(/\s/g, '')
  return translate(`administration.surveyEditor.question.${key}`)
}

const SurveyQuestions: React.FC<ISurveyQuestions> = ({
  blockId,
  instanceId,
  toolbar,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [questions, setQuestions] = useState<IQuestionData[]>([])
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [confirmationCallback, setConfirmationCallbackBase] = useState<
    (() => void) | null
  >(null)
  const confirmed = React.useRef(false)

  const setConfirmationCallback = (callback: () => void) => {
    if (confirmed.current) return callback()
    setConfirmationCallbackBase(() => () => callback())
  }

  useEffect(() => {
    dispatch({
      type: fromQuestionActions.GET_QUESTIONS_LIST,
      instanceId: instanceId,
    })
  }, [blockId, dispatch, instanceId])

  const { list } = useSelector(({ questions }: IMainState) => questions)
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)

  useEffect(() => {
    setQuestions(list)
  }, [list])

  const runGuarded = (callback: () => void) => {
    if (edit.hasResponses) return setConfirmationCallback(callback)
    callback()
  }

  const handleSave = (
    data: IQuestionLikertModel<BlockTypes.BlockSurvey> | IOpenEndedModel,
    type: string
  ) => {
    runGuarded(() => {
      switch (type) {
        case 'Questions::Likert':
          dispatch({
            type: fromQuestionActions.QUESTION_LIKERT_CREATE,
            data: { data, instanceId: instanceId },
          })
          break
        case 'Questions::OpenEnded':
          dispatch({
            type: fromQuestionActions.QUESTION_OPENENDED_CREATE,
            data: { data, instanceId: instanceId },
          })
          break
        default:
          break
      }
      setEditingId(null)
    })
  }

  const handleEdit = (id: number, data: unknown, type: string) => {
    runGuarded(() => {
      switch (type) {
        case 'Questions::Likert':
          dispatch({
            type: fromQuestionActions.QUESTION_LIKERT_EDIT,
            data: { data, instanceId: instanceId, questionId: id },
          })
          break
        case 'Questions::OpenEnded':
          dispatch({
            type: fromQuestionActions.QUESTION_OPENENDED_EDIT,
            data: { data, instanceId: instanceId, questionId: id },
          })
          break
        default:
          break
      }
      setEditingId(null)
    })
  }

  const handleDelete = (id: number, type: string) => {
    runGuarded(() => {
      switch (type) {
        case 'Likert':
          dispatch({
            type: fromQuestionActions.QUESTION_LIKERT_DESTROY,
            payload: { questionId: id, instanceId },
          })
          break
        case 'Open Ended':
          dispatch({
            type: fromQuestionActions.QUESTION_OPENENDED_DESTROY,
            payload: { questionId: id, instanceId },
          })
          break
        default:
          break
      }
    })
  }

  // Rows are keyed by the question id: a scale and an open question live in
  // different API tables and may share their `questionableId`.
  const editing =
    editingId === 'new'
      ? undefined
      : questions.find((question) => question.id === editingId)

  const prefix = 'administration.surveyEditor.list'

  return (
    <>
      <SurveyQuestionRows
        items={questions.map((question) => ({
          key: question.id,
          name: question.name,
          typeLabel: typeLabel(question.type, t),
          mandatory: question.mandatory,
        }))}
        toolbar={toolbar}
        rowTestId={(index) => `survey-question-${index + 1}`}
        onAdd={() => setEditingId('new')}
        onEdit={(key) => setEditingId(key)}
        onDelete={(key) => {
          const question = questions.find((item) => item.id === key)
          if (question) handleDelete(question.questionableId, question.type)
        }}
      />
      {editingId !== null && (
        <SurveyQuestionModal
          title={t(
            editingId === 'new' ? `${prefix}.add` : `${prefix}.editTitle`
          )}
          index={
            editingId === 'new'
              ? questions.length + 1
              : questions.findIndex((question) => question.id === editingId) + 1
          }
          data={editing}
          blockId={blockId}
          onSave={(data, type) =>
            editingId === 'new'
              ? handleSave(
                  data as IQuestionLikertModel<BlockTypes.BlockSurvey>,
                  type
                )
              : editing && handleEdit(editing.questionableId, data, type)
          }
          onClose={() => setEditingId(null)}
        />
      )}
      <Confirmation
        show={!!confirmationCallback}
        onAccept={() => {
          confirmationCallback?.()
          setConfirmationCallbackBase(null)
          confirmed.current = true
        }}
        onClose={() => setConfirmationCallbackBase(null)}
      />
    </>
  )
}

export default SurveyQuestions
